import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { CATEGORY_BY_VALUE, type EventCategory } from '../types';
import { format, formatDistanceToNow } from 'date-fns';
import { TrendingUp, MousePointerClick, Globe, Bot, Calendar as CalendarIcon } from 'lucide-react';

interface TopRow {
  id: string;
  title: string;
  category: EventCategory;
  total_clicks: number;
  clicks_7d: number;
  clicks_30d: number;
  last_click_at: string | null;
}

interface ClickRow {
  id: number;
  event_id: string;
  clicked_at: string;
  country: string | null;
  city: string | null;
  user_agent: string | null;
  utm_source: string | null;
  is_bot: boolean;
}

export const AnalyticsView: React.FC = () => {
  const [top, setTop] = useState<TopRow[]>([]);
  const [recent, setRecent] = useState<ClickRow[]>([]);
  const [totalAll, setTotalAll] = useState<number>(0);
  const [total7d, setTotal7d] = useState<number>(0);
  const [botRate, setBotRate] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400_000).toISOString();
      const [topRes, recentRes, totalRes, weekRes, botRes] = await Promise.all([
        supabase
          .from('top_clicked_events')
          .select('*')
          .order('clicks_7d', { ascending: false })
          .limit(20),
        supabase
          .from('link_clicks')
          .select('id, event_id, clicked_at, country, city, user_agent, utm_source, is_bot')
          .order('clicked_at', { ascending: false })
          .limit(50),
        supabase.from('link_clicks').select('id', { count: 'exact', head: true }).eq('is_bot', false),
        supabase
          .from('link_clicks')
          .select('id', { count: 'exact', head: true })
          .eq('is_bot', false)
          .gte('clicked_at', sevenDaysAgo),
        supabase.from('link_clicks').select('id', { count: 'exact', head: true }).eq('is_bot', true),
      ]);

      setTop((topRes.data ?? []) as TopRow[]);
      setRecent((recentRes.data ?? []) as ClickRow[]);
      setTotalAll(totalRes.count ?? 0);
      setTotal7d(weekRes.count ?? 0);
      const botCount = botRes.count ?? 0;
      const total = (totalRes.count ?? 0) + botCount;
      setBotRate(total > 0 ? botCount / total : 0);
      setLoading(false);
    };
    void load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-[#9945ff] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          icon={<MousePointerClick className="w-5 h-5" />}
          label="All-time clicks"
          value={totalAll.toLocaleString()}
          accent="#9945ff"
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Last 7 days"
          value={total7d.toLocaleString()}
          accent="#14f195"
        />
        <StatCard
          icon={<Bot className="w-5 h-5" />}
          label="Bot share"
          value={`${(botRate * 100).toFixed(1)}%`}
          accent="#ffd24a"
        />
      </div>

      <div className="sticker bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-xl sm:text-2xl uppercase tracking-tight text-[#0b0b0c]">
            Top events (7-day)
          </h3>
        </div>
        {top.length === 0 ? (
          <p className="text-sm text-[#0b0b0c]/55 italic">No tracked clicks yet.</p>
        ) : (
          <div className="space-y-2">
            {top.map((row, i) => {
              const cat = CATEGORY_BY_VALUE[row.category];
              return (
                <div
                  key={row.id}
                  className="sticker-soft bg-[#f3ecfc]/40 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <span className="font-bold text-2xl text-[#0b0b0c]/55 sm:w-8 sm:text-right">{i + 1}</span>
                  {cat && (
                    <span
                      className="px-2 py-0.5 rounded-full border-[1.5px] border-[#0b0b0c] text-[10px] font-bold uppercase tracking-wider shrink-0"
                      style={{
                        backgroundColor: cat.fill,
                        color: cat.textColor === 'white' ? '#fff' : '#0b0b0c',
                      }}
                    >
                      {cat.label}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-[#0b0b0c] truncate">{row.title}</p>
                    {row.last_click_at && (
                      <p className="text-[11px] text-[#0b0b0c]/55">
                        Last click {formatDistanceToNow(new Date(row.last_click_at), { addSuffix: true })}
                      </p>
                    )}
                  </div>
                  <div className="sm:text-right shrink-0">
                    <p className="font-bold text-2xl text-[#0b0b0c] leading-none">{row.clicks_7d}</p>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/55">
                      / {row.total_clicks} all
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="sticker bg-white p-4 sm:p-5">
        <h3 className="font-bold text-xl sm:text-2xl uppercase tracking-tight text-[#0b0b0c] mb-4">
          Recent clicks
        </h3>
        {recent.length === 0 ? (
          <p className="text-sm text-[#0b0b0c]/55 italic">No clicks recorded yet.</p>
        ) : (
          <div className="space-y-1">
            {recent.map((row) => (
              <div
                key={row.id}
                className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-3 py-2 rounded-lg ${
                  row.is_bot ? 'opacity-50' : ''
                }`}
              >
                <span className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55 sm:w-32 shrink-0">
                  {format(new Date(row.clicked_at), 'MMM d HH:mm')}
                </span>
                {row.country && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#0b0b0c]/65 shrink-0">
                    <Globe className="w-3 h-3" />
                    {row.country}
                    {row.city ? ` · ${row.city}` : ''}
                  </span>
                )}
                {row.utm_source && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#9945ff]">
                    via {row.utm_source}
                  </span>
                )}
                {row.is_bot && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#0b0b0c]/45 inline-flex items-center gap-1">
                    <Bot className="w-3 h-3" /> bot
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string; accent: string }> = ({
  icon, label, value, accent,
}) => (
  <div className="sticker bg-white p-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 sticker-soft flex items-center justify-center" style={{ background: accent }}>
        {icon}
      </div>
      <div>
        <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">{label}</p>
        <p className="font-bold text-xl sm:text-2xl text-[#0b0b0c]">{value}</p>
      </div>
    </div>
  </div>
);
